/**
 * Folder settings: rename/color (owner), members with roles (owner manages),
 * invites (owner), leave (members), delete (owner, typed confirm).
 */
import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Users, X } from "lucide-react";
import { create } from "zustand";
import {
  SHARED_FOLDER_COLORS,
  type SharedFolderColor,
  type SharedFolderInviteDto,
  type SharedFolderMemberDto,
} from "@hydra-notes/shared";
import { useAuth } from "@/data/auth";
import { sharedFoldersApi, sharedAccent, useSharedFolders, type SharedFolderMeta } from "@/data/sharedFolders";
import { Button } from "@/ui/Button";
import { Input } from "@/ui/Input";
import { IconButton } from "@/ui/IconButton";
import { toast } from "@/ui/toast";
import s from "./SignInDialog.module.css";
import sf from "./SharedFolder.module.css";

interface SharedFolderDialogStore {
  folderId: string | null;
  tab: "settings" | "members";
  open: (folderId: string, tab?: "settings" | "members") => void;
  close: () => void;
}
export const useSharedFolderDialog = create<SharedFolderDialogStore>((set) => ({
  folderId: null,
  tab: "settings",
  open: (folderId, tab = "settings") => set({ folderId, tab }),
  close: () => set({ folderId: null }),
}));

export function SharedFolderDialog() {
  const { folderId, close } = useSharedFolderDialog();
  const meta = useSharedFolders((st) => st.folders.find((f) => f.id === folderId));
  return (
    <Dialog.Root open={folderId !== null} onOpenChange={(o) => !o && close()}>
      <Dialog.Portal>
        <Dialog.Overlay className={s.overlay} />
        <Dialog.Content className={s.dialog}>
          {folderId && meta ? <FolderSettings key={folderId} folderId={folderId} meta={meta} onClose={close} /> : null}
          <Dialog.Close asChild>
            <IconButton label="Close" className={s.close}>
              <X size={14} />
            </IconButton>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function FolderSettings({ folderId, meta, onClose }: { folderId: string; meta: SharedFolderMeta; onClose: () => void }) {
  const me = useAuth((a) => a.user);
  const isOwner = meta.role === "owner";
  const [title, setTitle] = useState(meta.title);
  const [members, setMembers] = useState<SharedFolderMemberDto[]>([]);
  const [invites, setInvites] = useState<SharedFolderInviteDto[]>([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"editor" | "viewer">("editor");
  const [confirmDelete, setConfirmDelete] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const detail = await sharedFoldersApi.detail(folderId);
      setMembers(detail.members);
      if (isOwner) {
        const inv = await sharedFoldersApi.invites(folderId);
        setInvites(inv.invites);
      }
    } catch {
      // folder may have just been deleted under us
    }
  };
  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderId]);

  const saveTitle = async () => {
    const t = title.trim();
    if (!t || t === meta.title) return;
    try {
      await sharedFoldersApi.update(folderId, { title: t });
      await useSharedFolders.getState().refresh();
      toast.success("Folder renamed");
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const setColor = async (colorKey: SharedFolderColor) => {
    try {
      await sharedFoldersApi.update(folderId, { colorKey });
      await useSharedFolders.getState().refresh();
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const sendInvite = async () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email || busy) return;
    setBusy(true);
    try {
      const res = await sharedFoldersApi.invite(folderId, email, inviteRole);
      setInviteEmail("");
      if (res.link) {
        await navigator.clipboard.writeText(location.origin ? res.link : res.link).catch(() => {});
        toast.success("Invite created (email is off in dev); the accept link is on your clipboard.");
      } else {
        toast.success(`Invite sent to ${email}`);
      }
      await reload();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const removeFolder = async () => {
    if (confirmDelete !== meta.title) return;
    setBusy(true);
    try {
      await sharedFoldersApi.remove(folderId);
      useSharedFolders.getState().removeLocal(folderId);
      await useSharedFolders.getState().refresh();
      toast.info(`"${meta.title}" was deleted for every member.`);
      onClose();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const leave = async () => {
    setBusy(true);
    try {
      await sharedFoldersApi.leave(folderId);
      useSharedFolders.getState().removeLocal(folderId);
      await useSharedFolders.getState().refresh();
      toast.info(`You left "${meta.title}".`);
      onClose();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className={s.header}>
        <Users size={20} />
        <Dialog.Title className={s.title}>{meta.title}</Dialog.Title>
      </div>
      <Dialog.Description className={s.subtitle}>
        Shared folder, {meta.memberCount} member{meta.memberCount === 1 ? "" : "s"}. Contents count
        against every member's storage ({fmtBytes(meta.sizeBytes)}).
      </Dialog.Description>

      {isOwner ? (
        <>
          <div className={sf.sectionLabel}>Name and color</div>
          <div className={sf.inviteRow}>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} onBlur={() => void saveTitle()} onKeyDown={(e) => e.key === "Enter" && void saveTitle()} />
          </div>
          <div className={sf.swatches}>
            {SHARED_FOLDER_COLORS.map((key) => (
              <button
                key={key}
                type="button"
                title={key}
                className={[sf.swatch, key === meta.colorKey ? sf.swatchActive : ""].join(" ")}
                style={{ "--swatch": sharedAccent(key) } as React.CSSProperties}
                onClick={() => void setColor(key)}
              />
            ))}
          </div>
        </>
      ) : null}

      <div className={sf.sectionLabel}>Members</div>
      <div className={sf.memberList}>
        {members.map((m) => (
          <div key={m.userId} className={sf.memberRow}>
            <span className={sf.memberName}>
              {m.displayName || m.email}
              {m.userId === me?.id ? " (you)" : ""}
              <div className={sf.memberEmail}>{m.email}</div>
            </span>
            {isOwner && m.role !== "owner" ? (
              <>
                <select
                  className={sf.roleSelect}
                  value={m.role}
                  onChange={(e) => {
                    void sharedFoldersApi
                      .setMemberRole(folderId, m.userId, e.target.value as "editor" | "viewer")
                      .then(reload)
                      .catch((err: Error) => toast.error(err.message));
                  }}
                >
                  <option value="editor">editor</option>
                  <option value="viewer">viewer</option>
                </select>
                <Button
                  variant="ghost"
                  onClick={() => {
                    void sharedFoldersApi
                      .removeMember(folderId, m.userId)
                      .then(reload)
                      .then(() => useSharedFolders.getState().refresh())
                      .catch((err: Error) => toast.error(err.message));
                  }}
                >
                  Remove
                </Button>
              </>
            ) : (
              <span className={sf.roleTag}>{m.role}</span>
            )}
          </div>
        ))}
      </div>

      {isOwner ? (
        <>
          <div className={sf.sectionLabel}>Invite by email</div>
          <div className={sf.inviteRow}>
            <Input
              placeholder="teammate@example.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && void sendInvite()}
            />
            <select className={sf.roleSelect} value={inviteRole} onChange={(e) => setInviteRole(e.target.value as "editor" | "viewer")}>
              <option value="editor">editor</option>
              <option value="viewer">viewer</option>
            </select>
            <Button variant="solid" disabled={busy || !inviteEmail.trim()} onClick={() => void sendInvite()}>
              Invite
            </Button>
          </div>
          {invites.length > 0 ? (
            <>
              <div className={sf.sectionLabel}>Pending invites</div>
              <div className={sf.memberList}>
                {invites.map((inv) => (
                  <div key={inv.token} className={sf.memberRow}>
                    <span className={sf.memberName}>{inv.email}</span>
                    <span className={sf.roleTag}>{inv.role}</span>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        void sharedFoldersApi
                          .revokeInvite(folderId, inv.token)
                          .then(reload)
                          .catch((err: Error) => toast.error(err.message));
                      }}
                    >
                      Revoke
                    </Button>
                  </div>
                ))}
              </div>
            </>
          ) : null}
          <div className={sf.dangerZone}>
            <Input
              placeholder={`Type "${meta.title}" to delete`}
              value={confirmDelete}
              onChange={(e) => setConfirmDelete(e.target.value)}
            />
            <Button variant="ghost" disabled={confirmDelete !== meta.title || busy} onClick={() => void removeFolder()}>
              Delete folder
            </Button>
          </div>
          <p className={sf.hint}>
            Deleting removes the folder and all its notes for every member. This cannot be undone.
          </p>
        </>
      ) : (
        <div className={sf.dangerZone}>
          <span className={sf.hint}>Leaving removes this folder from your sidebar; your account stops paying its storage.</span>
          <Button variant="ghost" disabled={busy} onClick={() => void leave()}>
            Leave folder
          </Button>
        </div>
      )}
    </>
  );
}

function fmtBytes(n: number): string {
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(1)} GB`;
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${n} B`;
}
